import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'

import AuthCard from 'src/features/auth/components/auth-card'
import AuthContent from 'src/features/auth/components/auth-content'
import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import AuthSubtitle from 'src/features/auth/components/auth-subtitle'
import AuthTitle from 'src/features/auth/components/auth-title'
import { Button } from 'src/shared/components/ui/buttons/button'
import { Form } from 'src/shared/components/ui/forms/form'
import { useConfirmPasswordReset, useRequestPasswordReset } from 'src/shared/features/auth/api'
import AuthInputFormField from 'src/shared/features/auth/components/auth-input-form-field'
import { useToast } from 'src/shared/hooks/use-toast'
import type { RequestPasswordFormData, ResetPasswordFormData } from 'src/shared/schemas/forms/auth'
import { requestPasswordSchema, resetPasswordSchema } from 'src/shared/schemas/forms/auth'

import AuthCodeInput from '../auth-code-input'

interface NewPasswordFormProps {
  email: string
  handleBackToLogin: () => void
  onSuccess: () => void
}

const NewPasswordForm = ({ email, handleBackToLogin, onSuccess }: NewPasswordFormProps) => {
  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      code: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const resetPassword = useConfirmPasswordReset()
  const { toast } = useToast()

  const onResetPassword = async (data: ResetPasswordFormData) => {
    if (!email) {
      toast({
        title: 'Error',
        description: 'Email is required to reset password',
        variant: 'destructive',
      })
      return
    }

    try {
      await resetPassword.mutateAsync({
        email,
        code: data.code,
        newPassword: data.newPassword,
      })
      toast({
        title: 'Password Reset Successful',
        description: 'You can now log in with your new password.',
      })
      // navigate(paths.auth.login.getHref())
      onSuccess()
    } catch (error) {
      if (error instanceof Error) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        })
        toast({
          title: 'Having trouble?',
          description:
            "If you haven't received your code, please verify you entered your email correctly and check your spam folder. If you still can't find it, try signing up for an account again. For further issues contact support@joinconrad.ai",
          duration: 10000,
        })
      }
    }
  }

  return (
    <AuthCard contentClassName="h-fit py-0 md:py-0" className="px-0 md:px-0">
      {/* <AuthHeader headerForText="professionals" showHeaderButton={false} /> */}
      <AuthContent>
        <AuthTitle title="Enter new password" />
        <AuthSubtitle description="Enter the code we sent to your email and choose a new password" />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onResetPassword)} className="space-y-5">
            <div className="flex flex-col gap-2">
              <AuthCodeInput
                isConsumerLogin={false}
                control={form.control}
                name="code"
                className="rounded-[12px]"
                inputClassName="max-w-none h-[60px] border-none bg-[#F8F7F5] rounded-[12px]"
              />
              <AuthInputFormField control={form.control} name="newPassword" label="New Password" type="password" />
              <AuthInputFormField
                control={form.control}
                name="confirmPassword"
                label="Confirm Password"
                type="password"
              />
            </div>
            <AuthSubmitButton type="submit" className="bg-primary" isLoading={resetPassword.isPending}>
              {resetPassword.isPending ? 'Resetting...' : 'Reset password'}
            </AuthSubmitButton>
          </form>
        </Form>
        <Button variant="link" className="w-full text-sm text-muted underline" onClick={handleBackToLogin}>
          Back to login
        </Button>
      </AuthContent>
      {/* <AuthFooter /> */}
    </AuthCard>
  )
}

interface RequestPasswordResetFormProps {
  onSuccess: (email: string) => void
  handleBackToLogin: () => void
}

const RequestPasswordResetForm = ({ onSuccess, handleBackToLogin }: RequestPasswordResetFormProps) => {
  const form = useForm<RequestPasswordFormData>({
    resolver: zodResolver(requestPasswordSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
    },
  })

  const requestPassword = useRequestPasswordReset()
  const { toast } = useToast()

  const onRequestPassword = async (data: RequestPasswordFormData) => {
    try {
      await requestPassword.mutateAsync(data.email)
      // navigate(paths.auth.resetPassword.confirm.getHref(data.email))
      toast({
        title: 'Code Sent',
        description: `If an account for ${data.email} exists, you will receive a confirmation code.`,
      })
    } catch (error) {
      console.error(error)
      // navigate(paths.auth.resetPassword.confirm.getHref(data.email))
      toast({
        title: 'Code Sent',
        description: `If an account for ${data.email} exists, you will receive a confirmation code.`,
      })
    } finally {
      onSuccess(data.email)
    }
  }

  return (
    <AuthCard contentClassName="h-fit py-0 md:py-0" className="px-0 md:px-0">
      <AuthContent>
        <AuthTitle title="Reset your password" />
        <AuthSubtitle description="Enter your email address and we'll send you a code to reset your password" />
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onRequestPassword)} className="space-y-5">
            <AuthInputFormField control={form.control} name="email" label="Email" type="email" />
            <AuthSubmitButton type="submit" className="bg-primary" isLoading={requestPassword.isPending}>
              {requestPassword.isPending ? 'Sending code...' : 'Send reset code'}
            </AuthSubmitButton>
          </form>
        </Form>
        <Button variant="link" className="w-full text-sm text-muted underline" onClick={handleBackToLogin}>
          Back to login
        </Button>
      </AuthContent>
    </AuthCard>
  )
}

interface ResetPasswordFormProps {
  onSuccess: () => void
  handleBackToLogin: () => void
}

export default function ResetPasswordForm({ onSuccess, handleBackToLogin }: ResetPasswordFormProps) {
  const [step, setStep] = useState<'request' | 'new'>('request')
  const [email, setEmail] = useState('')

  if (step === 'request') {
    return (
      <RequestPasswordResetForm
        onSuccess={(email) => {
          setStep('new')
          setEmail(email)
        }}
        handleBackToLogin={handleBackToLogin}
      />
    )
  }

  return <NewPasswordForm email={email} handleBackToLogin={handleBackToLogin} onSuccess={onSuccess} />
}
